import { useEffect, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as apiProduct from 'api/apiProducts';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import { resolveDefaultDimensionId, resolveDefaultVendorId } from 'utils/helpers/catalogDefaults';
import ESpinner from 'components/others/Spinner';
import ProductFormik from './ProductFormik';
import { useProduct } from '../context/ProductProvider';
import type { ProductEditType } from '../types';

const emptyProduct: ProductEditType = {
    ID: 0,
    prodArticle: '',
    prodName: '',
    prodDescription: '',
    vendorId: 0,
    dimensionId: 0,
    categoryId: 0,
    prodCreated: Date.now(),
    prodUpdated: Date.now(),
};

interface OffProductCardProps {
    fetchProducts: (force?: boolean) => Promise<unknown>;
}

const OffProductCard = ({ fetchProducts }: OffProductCardProps) => {
    const { t } = useTranslation(['Products']);
    const { showEditProduct, setShowEditProduct } = useProduct();
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const [product, setProduct] = useState<ProductEditType | null>(null);

    const getCreateDefaults = () => ({
        vendorId: resolveDefaultVendorId(localSettings?.DIRS?.Vendor),
        dimensionId: resolveDefaultDimensionId(localSettings?.DIRS?.Dimension),
    });

    const fetchProduct = async () => {
        if (showEditProduct === 0) {
            setProduct({
                ...emptyProduct,
                ...getCreateDefaults(),
                ownerId: auth.user.ID,
                userName: auth.user.userName,
                userEmail: auth.user.userEmail,
                userPicture: auth.user.userPicture,
            });
        }

        if (typeof showEditProduct === 'number' && showEditProduct > 0) {
            const fetched = await apiProduct.getProduct(showEditProduct);
            if (fetched) setProduct(fetched);
        }

        if (typeof showEditProduct === 'number' && showEditProduct < 0) {
            const fetched = await apiProduct.getProduct(-showEditProduct);
            if (fetched) {
                setProduct({
                    ...fetched,
                    ID: 0,
                    prodArticle: '',
                    ...getCreateDefaults(),
                });
            }
        }
    };

    const handleClose = () => {
        setShowEditProduct(false);
        setTimeout(() => {
            setProduct(null);
        }, 300);
    };

    useEffect(() => {
        if (showEditProduct !== false) fetchProduct();
    }, [showEditProduct]);

    useEffect(() => {
        if (!product) return;
        const inputProdName = document.querySelector(
            product.ID === 0 ? 'input[name="prodArticle"]' : 'input[name="prodName"]',
        ) as HTMLInputElement | null;

        inputProdName?.focus();
    }, [product]);

    return (
        <Offcanvas show={showEditProduct !== false} onHide={handleClose} placement="end">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{t('titleEdit')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                {product ? (
                    <ProductFormik setProduct={setProduct} fetchProducts={fetchProducts} product={product} />
                ) : (
                    <ESpinner />
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffProductCard;
